import { RelatedType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMSDataV3 } from '@frontend/api-sms-data';
import { WritebackMessagesIO } from '../types';
import { UseWritebackV1MutationEndpointArgs, useWritebackV1Mutation } from './use-writeback-v1-mutation';

const RELATED_TYPE_OPTIMISTIC_ID = 'temp-related-id';
const RELATED_TYPE_WRITEBACK_ID = 'writeback-id';

type MutationContext<C = unknown> = {
  otherContext?: C;
  internalContext: {
    revertOptimisticUpdate: () => void;
  };
};

/**
 * A hook that returns a mutation for the `WritebackMessages` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useWritebackMessagesMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends {
    threadId: string;
  } = {
    threadId: string;
  },
  RequestOverride extends WritebackMessagesIO['input'] = WritebackMessagesIO['input']
>({
  options,
  optimisticUpdate = false,
  ...args
}: UseWritebackV1MutationEndpointArgs<'WritebackMessages', E, C | undefined, OtherOptions, RequestOverride> = {}) => {
  const { updateSMS } = SMSDataV3._QueryUpdaters.useQueryUpdaters();

  return useWritebackV1Mutation<'WritebackMessages', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'WritebackMessages',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        if (optimisticUpdate) {
          request.smsIds.forEach((smsId) => {
            updateSMS({
              matchValues: { id: smsId, locationId: request.locationId },
              newValues: (sms) => ({
                ...sms,
                relatedIds: [
                  ...sms.relatedIds,
                  { id: RELATED_TYPE_OPTIMISTIC_ID, type: RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE },
                ],
              }),
            });
          });
        }

        return {
          otherContext: await options?.onMutate?.(request),
          internalContext: {
            revertOptimisticUpdate: () => {
              if (optimisticUpdate) {
                request.smsIds.forEach((smsId) => {
                  updateSMS({
                    matchValues: { id: smsId, locationId: request.locationId },
                    newValues: (sms) => ({
                      ...sms,
                      relatedIds: sms.relatedIds.filter(
                        (relatedId) =>
                          relatedId.id !== RELATED_TYPE_OPTIMISTIC_ID ||
                          relatedId.type !== RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE
                      ),
                    }),
                  });
                });
              }
            },
          },
        };
      },
      onSuccess: (response, request, context) => {
        const smsIdsToRevert = request.smsIds.filter((smsId) => !response.smsIds.includes(smsId));
        const smsIdsToAddRelatedId = optimisticUpdate
          ? response.smsIds.filter((smsId) => !request.smsIds.includes(smsId))
          : response.smsIds;

        smsIdsToAddRelatedId.forEach((smsId) => {
          updateSMS({
            matchValues: { id: smsId, locationId: request.locationId },
            newValues: (sms) => {
              return {
                ...sms,
                relatedIds: [
                  ...sms.relatedIds,
                  { id: RELATED_TYPE_WRITEBACK_ID, type: RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE },
                ],
              };
            },
          });
        });
        smsIdsToRevert.forEach((smsId) => {
          updateSMS({
            matchValues: { id: smsId, locationId: request.locationId },
            newValues: (sms) => {
              return {
                ...sms,
                relatedIds: sms.relatedIds.filter(
                  (relatedId) =>
                    relatedId.id !== RELATED_TYPE_OPTIMISTIC_ID ||
                    relatedId.type !== RelatedType.RELATED_TYPE_SMS_WRITEBACK_NOTE
                ),
              };
            },
          });
        });

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        context?.internalContext.revertOptimisticUpdate();

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
